import axios from 'axios';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import {
  LOGOUT_API_URL,
  QP_LOGOUT_API_URL_PATH,
} from 'src/constants/app.constants';
import { useOptAuth } from 'src/contexts/auth.context';
import { useThirdPartyAppIntegration } from 'src/contexts/ThirdPartyAppsContext';
import { ThirdPartyAppType } from 'src/models/third-party-app-type.enum';
import { useIsPresenterNotesWindowUrlActive } from 'src/utils/hooks.util';
import { redirectTo } from 'src/utils/redirect.util';
import { openUrlInBrowserFromZoomApp } from 'src/utils/zoom-sdk.utils';
import * as AppUrls from '../../components/navbar/app.urls';
import styles from './header.module.css';
import { useIsGoogleSlidesUrlActive } from 'src/utils/hooks.util';
import { getFullQualifiedQpUrl } from 'src/utils/url.util';
import { LpSpinner } from '@livepolls/ui-components/src/components/spinner/LpSpinner';
import { WuAppHeader, WuIcon } from '@npm-questionpro/wick-ui-lib';
import { useLivePollEditorStore } from 'src/stores/useLivePollEditor.store';
import { livePollTitleSelector } from 'src/stores/livePoll-store.selectors';
import { URLS } from '../../components/navbar/app.urls';
import { isAnyRouteActive } from 'src/utils/isAnyRouteActive';

const logoutUrl = LOGOUT_API_URL!;
const zoomAppHome = AppUrls.MEETING_APPS_URLS.ZOOM_APP_HOME;

const editNavbarForUrls = [
  { text: 'WorkSpace', url: URLS.EDIT_LIVE_POLL_URL },
  { text: 'Sessions', url: URLS.SESSIONS_SESSION_LIST },
  { text: 'Dashboard', url: URLS.LIVE_POLL_SESSION_DASHBOARD },
  { text: 'Design', url: URLS.DESIGN_LIVE_POLL_URL },
];

interface Props {
  myAccount?: any;
  productCategories?: any;
}

export const Header = ({ myAccount, productCategories }: Props) => {
  const { t } = useTranslation();
  const { user } = useOptAuth();

  const [categoriesModal, setCategoriesModal] = React.useState<boolean>(false);
  const [accountModal, setAccountModal] = React.useState<boolean>(false);
  const livePollTitle = useLivePollEditorStore(livePollTitleSelector);
  const showAccountAndCategories = !!(myAccount && productCategories);
  const [isAdminPathActive, setIsAdminPathActive] =
    React.useState<boolean>(false);
  const location = useLocation();
  const [currentLocation, setCurrentLocation] = React.useState<string>(
    location.pathname,
  );
  const path = location.pathname;
  const { thirdPartyAppType } = useThirdPartyAppIntegration();
  const isGoogleSlidesUrlActive = useIsGoogleSlidesUrlActive();
  const isPresenterNotesWindowUrlActive = useIsPresenterNotesWindowUrlActive();
  const isActive = isAnyRouteActive(currentLocation, editNavbarForUrls);
  const [breadcrumbs, setBreadcrumbs] = React.useState<
    {
      type: string;
      label: string;
      onClick?: () => void;
    }[]
  >([]);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (isActive) {
      const title = getLivePollTitle(livePollTitle);
      setBreadcrumbs([
        {
          type: 'text',
          label: 'My Polls',
          onClick: () => {
            navigate('/livepolls');
          },
        },
        {
          label: title,
          type: 'text',
        },
      ]);
    } else {
      setBreadcrumbs([]);
    }
  }, [isActive, livePollTitle, navigate]);

  React.useEffect(() => {
    const pathAfterPrefix = '/' + AppUrls.URLS.ADMIN_PERFORMANCE_LOGS_URL;
    if (path === pathAfterPrefix) {
      setIsAdminPathActive(true);
    } else {
      setIsAdminPathActive(false);
    }
  }, [path, isAdminPathActive]);

  React.useEffect(() => {
    if (location.pathname !== currentLocation) {
      setCurrentLocation(location.pathname);
    }
  }, [currentLocation, location]);

  const logOut = async () => {
    if (thirdPartyAppType === ThirdPartyAppType.ZOOM_APP) {
      await logoutFromZoomApp();
    } else {
      await logoutFromEditApp();
    }
  };

  if (isGoogleSlidesUrlActive || isPresenterNotesWindowUrlActive) {
    return null;
  }

  const handleCategoryModal = () => {
    setCategoriesModal(!categoriesModal);
  };

  const handleAccountModal = () => {
    setAccountModal(!accountModal);
  };

  const customHeader = (
    <div className={styles.customHeader} data-testid="custom header">
      <div className={styles.customHeaderLeftSide}>
        <div className={styles.logoContainer}>
          <span
            className={styles.logo}
            onClick={() => (window.location.href = '/')}
          ></span>
        </div>
        <div className={styles.customHeaderText} onClick={handleCategoryModal}>
          <div className={styles.livepollHeader}>LivePolls</div>
          <div className={styles.downArrow}>
            {!categoriesModal && (
              <span className={`wm-arrow-drop-down ${styles.dropArrow}`}></span>
            )}
            {categoriesModal && (
              <span className={`wm-arrow-drop-up ${styles.dropArrow}`}></span>
            )}
          </div>
        </div>
      </div>
      <div className={styles.customHeaderRightSide}>
        <div className={styles.userInitials} onClick={handleAccountModal}>
          <span className={styles.userInitialsText}>{user?.initials}</span>
        </div>
      </div>

      {categoriesModal && (
        <div className={styles.categoryModal}>
          <LpSpinner message="Fetching..." />
        </div>
      )}
      {accountModal && (
        <div className={styles.accountModal}>
          <LpSpinner message="Fetching..." />
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.header} aria-label="header">
      {!showAccountAndCategories && customHeader}
      {showAccountAndCategories && (
        <WuAppHeader
          user={myAccount}
          categories={productCategories}
          onLogout={() => logOut()}
          productName={'LivePolls'}
        >
          <div className={styles.headerContent}>
            {breadcrumbs.map((breadcrumb, index) => (
              <div
                key={index}
                className={
                  index === 0 ? styles.firstBreadcrumb : styles.breadcrumb
                }
              >
                <span onClick={breadcrumb.onClick}>{breadcrumb.label}</span>
                {index === 0 && (
                  <WuIcon
                    icon="wm-arrow-forward-ios"
                    className={styles.rightIcon}
                  />
                )}
              </div>
            ))}
          </div>
        </WuAppHeader>
      )}
      {user?.isAdmin && (
        <Link
          to="/admin/logs"
          className={
            styles.admin +
            ' ' +
            (isAdminPathActive ? styles.activeActivePath : '')
          }
        >
          {t('admin')}
        </Link>
      )}
    </div>
  );
};

const logoutFromEditApp = async () => {
  const qpLogoutUrl = getFullQualifiedQpUrl(QP_LOGOUT_API_URL_PATH);
  try {
    await axios.get(qpLogoutUrl, { withCredentials: true });
    await axios.get(qpLogoutUrl, { withCredentials: true });
  } catch (error) {
  } finally {
    await axios.get(logoutUrl, { withCredentials: true });
    localStorage.clear();
    redirectTo(qpLogoutUrl);
  }
};

const logoutFromZoomApp = async () => {
  const qpLogoutUrl = getFullQualifiedQpUrl(QP_LOGOUT_API_URL_PATH);
  try {
    const isSuccess = await openUrlInBrowserFromZoomApp(qpLogoutUrl);
    if (!isSuccess)
      throw new Error('Error in ZoomSDK. Cannot open url to logout.');
  } catch (error) {
    console.error(error);
  } finally {
    await axios.get(logoutUrl, { withCredentials: true });
    localStorage.clear();
    redirectTo(zoomAppHome);
  }
};

const getLivePollTitle = (livePollTitle: string | undefined) => {
  if (!livePollTitle) {
    return 'Untitled';
  }
  if (livePollTitle.length > 20) {
    return livePollTitle.substring(0, 20) + '...';
  }
  return livePollTitle;
};
